import http from './http';
import store from '../store';
import { getApiRoute } from '../../config';
import { capitalizeFirstLetter } from '../utility';

const getQueryParams = () => {
  const parsedParams = [];
  const { config } = store.getters;

  const cpvUuid = `cpvUuid=${config.productId}`;
  const product = `product=${config.productFqn}`;
  parsedParams.push(cpvUuid, product);

  return `?count=10&${parsedParams.filter((param) => param !== '').join('&')}`;
};

const getPreviousResults = async (gameName) => {
  return gameName === 'crashcash' ? await getPreviousResultsCrashCash() : await getPreviousNGSResults(gameName);
};
const getPreviousResultsCrashCash = async () => {
  const { config } = store.getters;
  const results = await http.get(
    `${getApiRoute('historyURL', config)}${getApiRoute('gameResults', config)}${config.productInstanceUuid}`,
    {
      params: {
        size: 20,
      },
    },
  );

  results.data = formatPreviousResultsTime(results.data.slice(0, 10));
  return { results: results.data, game: 'CrashCash' };
};
const getPreviousNGSResults = async (gameName) => {
  const { config } = store.getters;
  const results = await http.get(
    `${getApiRoute('ngsURL', config)}${getApiRoute('gameResults', config)}${getQueryParams()}`,
  );
  return formatNGSResultsData(results.data, gameName);
};
const formatNGSResultsData = (results, gameName) => {
  const localGameConfig = store.getters.getGameGetter({ specificGame: gameName, getter: 'localGameConfig' });
  const { bettingInputs } = localGameConfig;
  const activeSpecialBets = bettingInputs.filter((bettingInput) => {
    return bettingInput.enabled && bettingInput.betType === 'special';
  });

  switch (gameName) {
    case 'luckysix':
    case 'keno':
    case 'luckyx': {
      results.forEach((result) => {
        result.draw = Object.values(result.draw);
        const specialBets = [];
        activeSpecialBets.forEach((activeSpecialBet) => {
          const specialBet = {
            name: store.getters.getTranslation(activeSpecialBet.betDisplayName),
            value: capitalizeFirstLetter(
              store.getters.getTranslation(calculateSpecialBetValue(activeSpecialBet, result)),
            ),
          };
          specialBets.push(specialBet);
        });
        const colorBet = {};
        result.specialBets = specialBets;
        result.colorBet = colorBet;
      });
      return results;
    }
    default: {
      // For races
      const productName = store.state.sevenProductsMapper[gameName];
      const data = { previousRounds: results, game: productName };
      data[productName] = { previousRounds: results };
      return data;
    }
  }
};

const calculateSpecialBetValue = (specialBet, result) => {
  const { currentActiveGame } = store.state;

  switch (currentActiveGame) {
    case 'luckysix':
      return calculateLuckySixSpecialBetValue(specialBet, result);
    case 'keno':
      return calculateKenoSpecialBetValue(specialBet, result);
    case 'luckyx':
      return calculateLuckyXSpecialBetValue(specialBet, result);
  }
};

const calculateLuckySixSpecialBetValue = (specialBet, result) => {
  const firstNumber = result.draw[0];
  let sum = 0;
  for (let i = 0; i < 5; i += 1) {
    sum += result.draw[i];
  }
  switch (specialBet.betId) {
    case 5: {
      const index = firstNumber % 8 === 0 ? 7 : (firstNumber % 8) - 1;
      return specialBet.outcomes[index];
    }
    case 6: {
      return sum > 122.5 ? specialBet.outcomes[1] : specialBet.outcomes[0];
    }
    case 7: {
      return specialBet.outcomes[firstNumber % 2];
    }
    case 8: {
      return firstNumber > 24 ? specialBet.outcomes[1] : specialBet.outcomes[0];
    }
    case 10: {
      return firstNumber;
    }
    case 11: {
      return specialBet.outcomes[sum % 2];
    }
  }
};

const calculateKenoSpecialBetValue = (specialBet, result) => {
  const firstNumber = result.draw[0];
  const lastNumber = result.draw[result.draw.length - 1];
  let outcome;

  let firstFiveSum = 0;
  for (const i of [0, 1, 2, 3, 4]) {
    firstFiveSum += result.draw[i];
  }

  let totalSum = 0;
  for (let i = 0; i < result.draw.length; i += 1) {
    totalSum += result.draw[i];
  }

  switch (specialBet.betId) {
    // First number under/over 40.5
    case 30:
      outcome = firstNumber > 40.5 ? specialBet.outcomes[1] : specialBet.outcomes[0];
      break;
    // Last number under/over 40.5
    case 31:
      outcome = lastNumber > 40.5 ? specialBet.outcomes[1] : specialBet.outcomes[0];
      break;
    // First number single digit
    case 32:
      outcome =
        Number.isInteger(firstNumber) && firstNumber >= -9 && firstNumber <= 9
          ? specialBet.outcomes[0]
          : specialBet.outcomes[1];
      break;
    // Last number single digit
    case 33:
      outcome =
        Number.isInteger(lastNumber) && lastNumber >= -9 && lastNumber <= 9
          ? specialBet.outcomes[0]
          : specialBet.outcomes[1];
      break;
    // First number odd/even
    case 34:
      outcome = firstNumber % 2 !== 0 ? specialBet.outcomes[0] : specialBet.outcomes[1];
      break;
    // Last number odd/even
    case 35:
      outcome = lastNumber % 2 !== 0 ? specialBet.outcomes[0] : specialBet.outcomes[1];
      break;
    // First number range
    case 36:
      specialBet.outcomes.forEach((range) => {
        const [min, max] = range.split(' - ').map(Number);
        if (firstNumber >= min && firstNumber <= max) outcome = range;
      });
      break;
    // Last number range
    case 37:
      specialBet.outcomes.forEach((range) => {
        const [min, max] = range.split(' - ').map(Number);
        if (lastNumber >= min && lastNumber <= max) outcome = range;
      });
      break;
    // First five numbers under/over 202.5
    case 38:
      outcome = firstFiveSum > 202.5 ? specialBet.outcomes[1] : specialBet.outcomes[0];
      break;
    // Total under/over 810.5
    case 39:
      outcome = totalSum > 810.5 ? specialBet.outcomes[1] : specialBet.outcomes[0];
      break;
  }

  return store.getters.getTranslation(outcome);
};

const calculateLuckyXSpecialBetValue = (specialBet, result) => {
  const firstNumber = result.draw[0];
  let outcome;

  let firstSixSum = 0;
  for (let i = 0; i < 5; i += 1) {
    firstSixSum += result.draw[i];
  }

  switch (specialBet.betId) {
    // Sum of first 6 numbers under/over 153
    case 21:
      outcome = firstSixSum > 153 ? specialBet.outcomes[1] : specialBet.outcomes[0];
      break;
    // First ball color
    case 22: {
      const index = firstNumber % 5 === 0 ? 4 : (firstNumber % 5) - 1;
      return specialBet.outcomes[index];
    }
    // First number odd/even
    case 24:
      outcome = firstNumber % 2 !== 0 ? specialBet.outcomes[1] : specialBet.outcomes[0];
      break;
    // First number (-25,5+)
    case 25:
      outcome = firstNumber > 25.5 ? specialBet.outcomes[1] : specialBet.outcomes[0];
      break;
  }

  return store.getters.getTranslation(outcome);
};

const formatPreviousResultsTime = (results) => {
  results.forEach((result) => {
    // format startedAt to a date object to print correct time
    // eslint-disable-next-line no-param-reassign
    result.startedAt = new Date(result.startedAt);
  });
  return results;
};
export default {
  getPreviousResults,
  formatPreviousResultsTime,
};
